import { AxiosRequestConfig } from "axios";
import { Packet } from "../Interfaces/commonDtos";
import {
  DropdownItem,
  OrderFilterOptions,
} from "../Interfaces/dropdownInterfaces";
import {
  IBulkOrderRow,
  IEditNLegOrderFormValues,
  INLegChanceSearchParams,
  INLegFileUploadResp,
  INLegOrderbooksBody,
  INLegOrderbooksResp,
  INLegOrderFillSearchParams,
  INLegsAddOrderFormValues,
  INLegsSearchParam,
  INLegsTableReport,
  INLegsTradeTableReport,
} from "../Interfaces/nLegsInterfaces";
import { Graph_Dto } from "../Interfaces/orderbookGraphInterfaces";
import { IShekarOrderMetricEventsResponse } from "../Interfaces/shekarMetricEvents";
import {
  ErrorTableReport,
  GetBookOrderResponse,
  ShekarChanceReport,
} from "../Interfaces/orderInterfaces";
import {
  IBrokerOrdersSearchParam,
  IStepOrderTableReport,
} from "../Interfaces/stepInterfaces";
import { Report } from "../Interfaces/TableInterfaces";
import {
  deleteRequest,
  getRequest,
  getTable,
  patchRequest,
  postDownloadExcel,
  postRequest,
  putRequest,
} from "./requestTemplates";

export const getNLegsOrderTable = ({
  legCount,
  offset,
  pageSize,
  searchParam,
  sortingType,
  additional,
  axiosOptions,
}: {
  searchParam: INLegsSearchParam;
  offset: number | null;
  pageSize: number | null;
  sortingType?: string;
  legCount: number;
  additional?: DropdownItem;
  axiosOptions?: AxiosRequestConfig;
}): Promise<Report<INLegsTableReport>> =>
  getTable<INLegsSearchParam, INLegsTableReport>(
    `/v1/nlegsorder/getOrderTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
    {
      name: "LegCount",
      id: legCount,
    }
  );

export const getNLegsOrderFilterOptions = ({
  searchParam,
  legCount,
}: {
  searchParam: INLegsSearchParam;
  legCount: number;
}): Promise<OrderFilterOptions> =>
  postRequest(
    `/v1/nlegsorder/getOrderFilterOptions?LegCount=${legCount}`,
    searchParam,
  );

export const getNLegsTradeFilterOptions = ({
  searchParam,
  legCount,
}: {
  searchParam: INLegsSearchParam;
  legCount: number;
}): Promise<OrderFilterOptions> =>
  postRequest(
    `/v1/nlegsorder/getTradeFilterOptions?LegCount=${legCount}`,
    searchParam,
  );

export const getNLegsTradeTable = ({
  offset,
  pageSize,
  searchParam,
  sortingType,
  legCount,
}: {
  searchParam: INLegsSearchParam;
  offset: number | null;
  pageSize: number | null;
  sortingType?: string;
  legCount: number;
  additional?: DropdownItem;
  axiosOptions?: AxiosRequestConfig;
}): Promise<Report<INLegsTradeTableReport>> =>
  getTable<INLegsSearchParam, INLegsTradeTableReport>(
    `/v1/nlegsorder/getTradeTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
    {
      name: "LegCount",
      id: legCount,
    }
  );

export const getNLegsErrorsTable = (
  searchParam: INLegsSearchParam,
  offset: number | null,
  pageSize: number | null,
  legCount: number
): Promise<Report<ErrorTableReport>> =>
  getTable<INLegsSearchParam, ErrorTableReport>(
    "v1/nlegsorder/getErrorTable",
    searchParam,
    offset,
    pageSize,
    undefined,
    { id: legCount, name: "LegCount" }
  );

export const getNLegsErrorFilterOptions = ({
  searchParam,
  legCount,
}: {
  searchParam: INLegsSearchParam;
  legCount: number;
}): Promise<OrderFilterOptions> =>
  postRequest(
    `/v1/nlegsorder/getErrorFilterOptions?LegCount=${legCount}`,
    searchParam,
  );

export const addNLegsBulkOrder = (
  orderRows: Array<IBulkOrderRow> | null
): Promise<Report<ErrorTableReport>> =>
  postRequest("/v1/nlegsorder/addBulkOrder", orderRows);

export const uploadNLegsOrderFile = (
  searchParam: FormData | null,
  legCount?: number
): Promise<Report<INLegFileUploadResp>> =>
  getTable<FormData | null, INLegFileUploadResp>(
    "/v1/nlegsorder/uploadOrderFile",
    searchParam,
    null,
    null,
    undefined,
    legCount
      ? {
          id: legCount,
          name: "LegCount",
        }
      : undefined
  );

export const getNLegOrderbooks = (
  body: INLegOrderbooksBody, signal?: AbortSignal
): Promise<INLegOrderbooksResp> =>
  postRequest("/v1/nlegorder/getOrderBooks", body, { signal });

export const addNLegOrder = (
  formValues: INLegsAddOrderFormValues
): Promise<Packet<boolean>> =>
  postRequest("/v1/nlegorder/AddOrder", formValues);

export const previewNLegOrderByVolume = (
  formValues: INLegsAddOrderFormValues
): Promise<INLegsAddOrderFormValues[]> =>
  postRequest("/v1/nlegorder/previewOrderByVolume", formValues);

export const submitNLegOrderByVolume = (
  orders: INLegsAddOrderFormValues[]
): Promise<Packet<boolean>> =>
  postRequest("/v1/nlegorder/submitOrderByVolume", orders);

export const getNLegsOrderFillTable = (
  searchParam: INLegOrderFillSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<any>> =>
  getTable<INLegOrderFillSearchParams, any>(
    "/v1/nlegorder/sahraOrderFillTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const cancelNLegsSahraFillOrder = (body: {
  sahraOrderId?: number;
}): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/nlegorder/cancelSahraOrder`, body);

export const bulkCancelNLegsOrder = (ids: number[]): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/nlegorder/bulkcancel`, ids);

export const cancelAllNLegsOrders = (
  searchParams: INLegsSearchParam,
  legCount: number
): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/nlegorder/cancelAll?LegCount=${legCount}`, searchParams);

export const bulkPauseNLegOrder = (
  selectedRowsIds: number[]
): Promise<Packet<boolean>> =>
  putRequest(`/v1/nlegorder/bulkpause`, selectedRowsIds);

export const pauseAllNLegsOrders = (
  searchParams: INLegsSearchParam,
  legCount: number
): Promise<Packet<boolean>> =>
  putRequest(`/v1/nlegorder/pauseAll?LegCount=${legCount}`, searchParams);

export const bulkResumeNLegOrder = (
  selectedRowsIds: number[]
): Promise<Packet<boolean>> =>
  putRequest(`/v1/nlegorder/bulkresume`, selectedRowsIds);

export const resumeAllNLegsOrders = (
  searchParams: INLegsSearchParam,
  legCount: number
): Promise<Packet<boolean>> =>
  putRequest(`/v1/nlegorder/resumeAll?LegCount=${legCount}`, searchParams);

export const cancelBulkNLegSahraFillOrder = (body: {
  nLegOrderIds?: number[];
}): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/nlegorder/cancelBulkSahraOrder`, body);

export const getNLegGraph = async (
  upairId: number
): Promise<Array<Graph_Dto>> =>
  getRequest(`/v1/nlegorder/graph?NLegOrderId=${upairId}`);

export const getNLegOrderEvents = (
  algorithmOrderId: number,
  conditionId: number,
  windowLen: number = 5,
  axiosOptions?: AxiosRequestConfig
): Promise<IShekarOrderMetricEventsResponse> =>
  getRequest(
    `/v1/nlegsorder/events?algorithm_order_id=${algorithmOrderId}&condition_id=${conditionId}&windowLen=${windowLen}`,
    axiosOptions
  );

export const cancelNLegOrder = (id: number): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/nlegorder/cancel/${id}`);

export const pauseNLegOrder = (orderId: number): Promise<Packet<boolean>> =>
  putRequest(`/v1/nlegorder/pause/${orderId}`);

export const resumeNLegOrder = (orderId: number): Promise<Packet<boolean>> =>
  putRequest(`/v1/nlegorder/resume/${orderId}`);

export const getNLegSahraOrderTable = (
  searchParam: INLegOrderFillSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<any>> =>
  getTable<INLegOrderFillSearchParams, any>(
    "/v1/nlegorder/sahraOrderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getNLegShekarChanceTable = (
  searchParam: INLegChanceSearchParams,
  offset: number | null,
  pageSize: number | null
): Promise<Report<ShekarChanceReport>> =>
  getTable<INLegChanceSearchParams, ShekarChanceReport>(
    "/v1/nlegorder/shekarChanceTable",
    searchParam,
    offset,
    pageSize
  );

export const editNLegOrder = (
  formValues: IEditNLegOrderFormValues
): Promise<Packet<boolean>> => patchRequest("/v1/nlegorder/edit", formValues);

export const getNLegBrokerOrdersTable = (
  searchParam: IBrokerOrdersSearchParam,
  offset: number | null,
  pageSize: number | null
): Promise<Report<IStepOrderTableReport>> =>
  getTable<IBrokerOrdersSearchParam, IStepOrderTableReport>(
    "/v1/nlegorder/getBrokerOrders",
    searchParam,
    offset,
    pageSize
  );

export const getNLegTradeExcel = (searchParam: INLegsSearchParam) => {
  const rand = Math.random();
  return postDownloadExcel(`/v1/nlegorder/tradeExcel?${rand}`, searchParam);
};

export const getOffsetTypes = async (legCount: number): Promise<DropdownItem[]> => {
  const response = await getRequest<{ title: { title: string; orderTypes: number[] }[] }>(
    `/v1/nlegs/offestdropdown/${legCount}`,
  );
  return response.title.map((item) => ({ id: item.orderTypes.join(","), name: item.title }));
};
